import { AccommodationProps } from "@/components/Accommodation/AccommodationProps.ts";
import Gallery from "@/components/Gallery/Gallery.tsx";
import { ChevronRight } from "@/components/Icons/utility/ChevronRight.tsx";
import { RatesPicker } from "@/components/Inputs/RatesPicker/RatesPicker.tsx";
import { PriceAndCapacity } from "@/components/PriceAndCapacity/PriceAndCapacity.tsx";
import { useGetLowestPriceFrom } from "@/helpers/getLowestPriceFrom.ts";
import { handleGTMPreviewAccommodationClick } from "@/helpers/gtm/handleGTMPreviewAccommodationClick.ts";
import { useScreenSize } from "@/helpers/hooks/useScreenSize.tsx";
import { useGetRGTranslated } from "@/helpers/translation/useGetRGTranslated.ts";
import { trimStringToLength } from "@/helpers/trimStringToLength";
import { MouseEvent, useMemo } from "react";
import { useTranslation } from "react-i18next";

export const Accommodation = (props: AccommodationProps) => {
  const { t } = useTranslation();
  const screenSize = useScreenSize();
  const rg = props.rateGroup;
  const areAllRPsUnavailable = rg.ratePlans.every((rp) => !rp.isAvailable);
  const { name, description } = useGetRGTranslated(rg);
  const lowestPriceFrom = useGetLowestPriceFrom(rg);

  // todo: uncomment when ready
  // const guestCapacity = rg.guestAdditionalCapacity
  //   ? `${rg.guestCapacity}+${rg.guestAdditionalCapacity}`
  //   : rg.guestCapacity;
  const guestCapacity = rg.guestCapacity;
  const handleOpenPreviewModal = (e: MouseEvent) => {
    let target: HTMLElement | null = e.target as HTMLElement;
    while (target != null) {
      if (target?.className?.includes?.("c-gallery__button")) return;
      if (target?.id === "rateGroupRates") return;
      target = target.parentElement;
    }

    props.handleOpenPreviewModal(rg);
    handleGTMPreviewAccommodationClick(rg);
    e.stopPropagation();
  };

  const sortedImages = useMemo(() => {
    const sortedPerGalleryPositionIndex = rg.images.sort((a, b) => {
      return (a.galleryPositionIndex ?? 0) - (b.galleryPositionIndex ?? 0);
    });

    const primaryImage = sortedPerGalleryPositionIndex.find((image) => image.isPrimary);
    if (primaryImage) {
      const filteredImages = sortedPerGalleryPositionIndex.filter((image) => !image.isPrimary);
      return [primaryImage, ...filteredImages];
    }
    return sortedPerGalleryPositionIndex;
  }, [rg.images]);

  return (
    <div
      onClick={handleOpenPreviewModal}
      className={`accommodation c-accommodation ${areAllRPsUnavailable ? "unavailableAcc" : undefined}`}
    >
      {areAllRPsUnavailable && <div className="unavailableAccMessage">{t("notAvailableRP")}</div>}
      <div className="accommodationImages c-accommodation__gallery">
        <Gallery
          disabled={areAllRPsUnavailable}
          slides={sortedImages}
          options={{ loop: true }}
          thumbnails={false}
        />
      </div>
      <div
        className={`accommodationInfoWrap c-accommodation__info${
          areAllRPsUnavailable ? "--unavailable" : ""
        }`}
      >
        <h3 className="u-h5 u-fw-600 c-accommodation__info-title">{name}</h3>
        <PriceAndCapacity
          priceFrom={lowestPriceFrom}
          capacity={guestCapacity}
          additionalCapacity={rg.guestAdditionalCapacity}
        />
        <div className="u-p1 c-accommodation__info-description">
          {trimStringToLength({ text: description || "", length: screenSize.width > 1200 ? 300 : 160 })}
        </div>
        <div className="c-accommodation__view-details">
          <button className="c-be-link" onClick={handleOpenPreviewModal}>
            <span className="c-be-link__label">{t("viewDetails")}</span>
            <span className="c-be-link__suffix">
              <ChevronRight />
            </span>
          </button>
        </div>
      </div>
      {!props.hideRatePlanList && (
        <div id="rateGroupRates" className="c-accommodation__rates">
          <RatesPicker rateGroup={rg} />
        </div>
      )}
    </div>
  );
};
